import React from "react";
import { FormRenderer } from "../form-fields/form-render/form-renderer";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { DragAndDrop } from "../ui/DragAndDrop";
import { FileText, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";

export const DrawerForm = ({
  title,
  fields,
  values,
  onFieldChange,
  onFieldBlur = () => {},
  fieldValues,
  footerLabel,
  cancelBtnLabel = "Cancel",
  submitBtnLabel = "Submit",
  cancelBtnDisabled = false,
  submitBtnDisabled = false,
  cancelBtnVariant = "outline",
  submitBtnVariant = "primary",
  onCancel = () => {},
  onSubmit = () => {},
  isOpen = false,
  componentContext = {},
  customClasses = {},
  children,
  // Drag and drop props
  showDragAndDrop = false,
  dragAndDropProps = {},
  documents = [],
  onDocumentDelete = () => {},
  docFieldChange = () => {},
}) => {
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <SheetContent
        side="right"
        className={cn(
          "w-[60%] sm:max-w-none flex flex-col h-full p-0 bg-primary-7",
          customClasses?.containerClassName
        )}
      >
        {/* Header */}
        <SheetHeader
          className={cn(
            "p-5 border-b border-neutral-5 bg-primary-5 text-left",
            customClasses?.headerClassName
          )}
        >
          <SheetTitle className="text-xl font-semibold text-neutral-12">
            {title}
          </SheetTitle>
        </SheetHeader>

        {/* Form Content */}
        <div
          className={cn(
            "flex-1 overflow-y-auto p-5 bg-primary-7",
            customClasses?.contentClassName
          )}
        >
          {children ? (
            children
          ) : (
            <>
              <div className="-mx-2 flex flex-wrap">
                {fields.map((item) => (
                  <FormRenderer
                    key={item.uniqueField}
                    item={item}
                    fieldValue={values[item.uniqueField]}
                    onFieldBlur={onFieldBlur}
                    onFieldChange={onFieldChange}
                    fieldValues={fieldValues}
                    componentContext={componentContext}
                    customClasses={customClasses}
                  />
                ))}
              </div>

              {/* Drag and Drop Section */}
              {showDragAndDrop && (
                <div className="mt-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-neutral-12 mb-2">
                      Upload Documents{" "}
                      <span className="text-neutral-7 ml-1 text-sm font-medium">
                        (Optional)
                      </span>
                    </h3>
                    <DragAndDrop
                      {...dragAndDropProps}
                      showLoaderSection={
                        componentContext?.assetId ? true : false
                      }
                    />
                  </div>

                  {/* Document List */}
                  {documents && documents.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-md font-medium text-neutral-12 mb-3">
                        Documents ({documents.length})
                      </h4>
                      <div className="bg-white rounded-lg border border-neutral-4 overflow-hidden">
                        {/* Header */}
                        <div className="grid grid-cols-12 gap-4 p-4 bg-primary-5 border-b border-neutral-4 font-medium text-sm text-neutral-10">
                          <div className="col-span-6 text-neutral-8">
                            Document
                          </div>
                          <div className="col-span-4 text-neutral-8">
                            Statement Date
                          </div>
                          <div className="col-span-2 text-center text-neutral-8">
                            Action
                          </div>
                        </div>

                        {/* Document Rows */}
                        <div className="divide-y divide-neutral-4">
                          {documents.map((doc, index) => (
                            <div
                              key={doc.documentId || index}
                              className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-gray-50"
                            >
                              {/* Document Info */}
                              <div className="col-span-6 flex items-center space-x-3">
                                <div className="w-8 h-8 bg-neutral-4 rounded flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-5 h-5 text-neutral-8" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-neutral-12 truncate">
                                    {doc.name}
                                  </p>
                                  <p className="text-xs text-neutral-9">
                                    {doc.documentType}
                                  </p>
                                </div>
                              </div>

                              {/* Statement Date */}
                              <div className="col-span-4">
                                <div className="w-full">
                                  <FormRenderer
                                    item={{
                                      uniqueField: `statementDate_${index}`,
                                      pathToValue: `statementDate_${index}`,
                                      type: "date",
                                      placeholder: "MM/dd/yyyy",
                                      label: "",
                                      hideLabel: true,
                                      xs: 12,
                                      disableFuture: true,
                                    }}
                                    fieldValue={doc.statementDate}
                                    onFieldChange={(
                                      path,
                                      value,
                                      type,
                                      item
                                    ) => {
                                      // Use the provided docFieldChange function
                                      docFieldChange(value, doc, index, {
                                        validationError: null,
                                      });
                                    }}
                                    onFieldBlur={onFieldBlur}
                                    fieldValues={fieldValues}
                                    componentContext={componentContext}
                                  />
                                </div>
                              </div>

                              {/* Delete Action */}
                              <div className="col-span-2 flex justify-center">
                                <button
                                  onClick={() => onDocumentDelete(doc, index)}
                                  className="text-red-500 hover:text-red-700 p-2 rounded hover:bg-red-50 transition-colors"
                                  title="Delete document"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className={`border-t border-neutral-5 p-5 flex flex-row ${
            footerLabel ? "justify-between" : "justify-end"
          } items-center bg-white`}
        >
          {footerLabel && (
            <div className="flex-1">
              <div className="text-sm text-neutral-10">{footerLabel}</div>
            </div>
          )}
          <div className="flex space-x-3 flex-shrink-0 max-lg:w-full">
            <Button
              variant={cancelBtnVariant}
              onClick={onCancel}
              className={cn(
                "px-6 min-w-[120px]",
                customClasses?.cancelBtnClassName
              )}
              disabled={cancelBtnDisabled}
            >
              {cancelBtnLabel}
            </Button>
            <Button
              variant={submitBtnVariant}
              onClick={onSubmit}
              className={cn("min-w-[120px]", customClasses?.submitBtnClassName)}
              disabled={submitBtnDisabled}
            >
              {submitBtnLabel}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
